import React, {
    useContext,
    useRef,
    useState,
    useEffect,
    useCallback,
    createContext,
} from "react";
import type { FC, PropsWithChildren, RefObject } from "react";

import { useOnClickOutside } from "../../../hooks/useOnClickOutside";
import { getKeyboardFocusableElements } from "../../../utils/dom/dom";

export interface IOptions {
    /** Открыть окно сразу при рендере? */
    isOpen?: boolean;
    /** Использовать роль menuitemradio, вместо menuitem? */
    withSelect?: boolean;
    /** Открыть окно после нажатия на стрелочку вправо? */
    openByArrowRight?: boolean;
    /** Открыть окно после нажатия на кнопку? */
    openMenuAfterClick?: boolean;
    /** Открыть окно после фокуса на кнопку? */
    openMenuAfterFocus?: boolean;
    /** Открыть окно после наведения мыши на кнопку? */
    openMenuAfterMouseEnter?: boolean;
    /** Через какое время показать окно после наведения мыши на кнопку? */
    openMenuAfterMouseEnterTimeout?: number;
    /** Закрыть окно после нажатия на стрелочку влево? */
    closeByArrowLeft?: boolean;
    /** Закрыть окно после нажатия на Escape? */
    closeByEscape?: boolean;
    /** Закрыть окно после нажатия на Tab? */
    closeByTab?: boolean;
    /** Закрыть окно, если фокус пропал с элементов, принадлежащих меню? */
    closeMenuAfterBlur?: boolean;
    /** Закрыть окно после выбора элемента? */
    closeMenuAfterSelect?: boolean;
    /** Закрыть окно после того, как мышь вышла за пределы окна? */
    closeMenuAfterMouseLeave?: boolean;
    /** Закрыть окно при нажатии за пределами окна? */
    closeMenuAfterClickOutside?: boolean;
    /** Через какое время скрыть окно после того, как мышь вышла за пределы окна? */
    closeMenuAfterMouseLeaveTimeout?: number;
    /** Сфокусироваться на активный элемент после открытия меню? */
    focusToActiveElementAfterOpen?: boolean;
    /** Сфокусироваться на кнопке после выбора элемента? */
    focusToButtonAfterSelect?: boolean;
    /** Слушатель открытия окна */
    onOpen?: () => void;
    /** Слушатель закрытия окна */
    onClose?: () => void;
}

const defaultOptions: IOptions = {
    isOpen: false,
    withSelect: true,
    openByArrowRight: false,
    openMenuAfterClick: true,
    openMenuAfterFocus: false,
    openMenuAfterMouseEnter: false,
    openMenuAfterMouseEnterTimeout: 300,
    closeByArrowLeft: false,
    closeByEscape: true,
    closeByTab: true,
    closeMenuAfterBlur: true,
    closeMenuAfterSelect: true,
    closeMenuAfterMouseLeave: false,
    closeMenuAfterClickOutside: true,
    closeMenuAfterMouseLeaveTimeout: 300,
    focusToActiveElementAfterOpen: true,
    focusToButtonAfterSelect: true,
};

interface IUseInitDropdownStore extends IOptions {
    /** Уровень вложенности меню */
    level: number;
    /** Массив со всеми открытыми меню */
    menuRefs: RefObject<HTMLElement[]>;
}

const useInitDropdownStore = (_options: IUseInitDropdownStore) => {
    const { level, menuRefs, ...options }: IUseInitDropdownStore = {
        ...defaultOptions,
        ..._options,
    };
    const { focusToActiveElementAfterOpen } = options;

    /** Ссылка на корневой контейнер меню (Dropdown) */
    const containerRef = useRef<HTMLDivElement | null>(null);
    /** Ссылка на элемент кнопки меню (Dropdown.Button) */
    const buttonRef = useRef<HTMLElement | null>(null);
    /** Таймер, по истечению которого откроется окно */
    const openTimerRef = useRef<ReturnType<typeof setTimeout>>();
    /** Таймер, по истечению которого закроется окно */
    const closeTimerRef = useRef<ReturnType<typeof setTimeout>>();
    /** Флаг отмены блюра. Актуально в тех случаях, когда клик происходит не по элементам меню, но в пределах окна. */
    const preventBlurRef = useRef<boolean>(false);
    /** Ссылка на элемент контейнера выпадающего меню (Dropdown.Menu) */
    const menuRef = useRef<HTMLElement | null>(null);

    /** Состояние окна: открыто/закрыто */
    const [isOpen, setIsOpen] = useState(options.isOpen || false);
    /** Флаг фокусировки на активный элемент в меню */
    const [withFocusOnActiveElement, setWithFocusOnActiveElement] = useState(
        focusToActiveElementAfterOpen,
    );

    /** Является ли меню вложенным? */
    const isSubMenu = level > 0;

    /**
     * Проверяем, принадлежит ли данный элемент нашему меню
     */
    const isOurElement = useCallback(
        (el: Element | null) => {
            if (!el) {
                return false;
            }
            if (buttonRef.current?.contains(el)) {
                return true;
            }
            // Всегда будет массив
            const menuStack = menuRefs.current!;
            return menuStack.some((menu, index) => {
                if (menu.contains(el)) {
                    return level <= index;
                }
                if (index === menuStack!.length - 1) {
                    return false;
                }
                return false;
            });
        },
        [level, menuRefs],
    );

    const focusToMenuButton = useCallback(() => {
        buttonRef.current?.focus();
    }, []);

    const focusToFirstElement = useCallback(() => {
        const menu = menuRef.current;
        if (menu) {
            getKeyboardFocusableElements(menu)[0]?.focus();
        }
    }, []);

    const openMenu = useCallback(() => {
        setIsOpen(true);
    }, []);

    const openMenuByMouseEnter = useCallback(() => {
        clearTimeout(closeTimerRef.current);
        if (options.openMenuAfterMouseEnter) {
            openTimerRef.current = setTimeout(() => {
                openMenu();
                setWithFocusOnActiveElement(false);
            }, options.openMenuAfterMouseEnterTimeout);
        }
    }, [
        openMenu,
        options.openMenuAfterMouseEnter,
        options.openMenuAfterMouseEnterTimeout,
    ]);

    const closeMenu = useCallback(() => {
        setIsOpen(false);
    }, [setIsOpen]);

    /**
     * Закрываем окно, если клик сделан за пределами окна
     */
    const clickOutsideHandler = useCallback(
        (e: MouseEvent | TouchEvent) => {
            if (!options.closeMenuAfterClickOutside) {
                return;
            }
            const el = e.target as HTMLElement;
            if (!isOurElement(el)) {
                closeMenu();
            }
        },
        [options.closeMenuAfterClickOutside, isOurElement, closeMenu],
    );

    useOnClickOutside(menuRef, clickOutsideHandler, {
        isOpen,
    });

    useEffect(() => {
        setIsOpen(options.isOpen || false);
    }, [options.isOpen]);

    useEffect(
        () => () => {
            clearTimeout(openTimerRef.current);
            clearTimeout(closeTimerRef.current);
        },
        [],
    );

    return {
        level,
        options,
        containerRef,
        buttonRef,
        closeTimerRef,
        preventBlurRef,
        menuRef,
        menuRefs,
        isOpen,
        setIsOpen,
        withFocusOnActiveElement,
        setWithFocusOnActiveElement,
        isOurElement,
        isSubMenu,
        focusToMenuButton,
        openMenu,
        openMenuByMouseEnter,
        closeMenu,
        focusToFirstElement,
    };
};

const Context = createContext({} as ReturnType<typeof useInitDropdownStore>);
export const useDropdownStore = () => useContext(Context);

interface IDropdownProvider {
    options: IOptions;
}

/**
 * Оптимизация лишних ререндеров компонентов. Более подробно можно почитать здесь:
 * https://wiki.odkl.ru/pages/viewpage.action?pageId=190187886
 */
export const DropdownProvider: FC<PropsWithChildren<IDropdownProvider>> = ({
    children,
    options,
}) => {
    const menuRefs = useRef<HTMLElement[]>([]);

    const { level: parentLevel = -1, menuRefs: parentMenuRefs = menuRefs } =
        useDropdownStore();

    const store = useInitDropdownStore({
        ...options,
        level: parentLevel + 1,
        menuRefs: parentMenuRefs,
    });

    // eslint-disable-next-line ok-custom-rules/check-attribute
    return <Context.Provider value={store}>{children}</Context.Provider>;
};
